import { getLocalTimeZone, today } from "@internationalized/date";
import { z } from "zod/v4";
import { DateFilterValue } from "#/modules/school.lessen/params.lessen-overview";

/** Resolves "today" at call time — must stay lazy, NOT a module-scope constant (a long-lived
 * server process would otherwise treat its boot day as "today" forever). */
function getDefaultLessonsDate(): string {
  return today(getLocalTimeZone()).toString();
}

/**
 * Search params schema for lessons overview: date and dateFilterValue.
 * Defaults: today's calendar date (resolved lazily at parse time via zod's default-factory form,
 * so it re-evaluates on every `validateSearch` call instead of freezing at module load),
 * `DateFilterValue.Equal`.
 */
export const lessonsSearchSchema = z.object({
  date: z.string().default(getDefaultLessonsDate),
  // Silently fall back to default for invalid `dateFilterValue` (don't fail route validation).
  dateFilterValue: z
    .nativeEnum(DateFilterValue)
    .default(DateFilterValue.Equal)
    .catch(DateFilterValue.Equal),
  // Set by the les-flow "save" redirect to briefly highlight the just-created lesson; stripped
  // from the URL (via `LESSONS_SEARCH_DEFAULTS`) once its one-time animation has run. Optional so
  // it stays out of the loader's narrowed deps type (it's a UI-only, client-side concern).
  highlightLesson: z.string().nullable().optional(),
});

/**
 * Default values for lessons search params; values not explicitly set will be omitted from URL.
 * NOTE: `date` here is evaluated once, at module load, for `stripSearchParams` comparison — this
 * is a static snapshot (unavoidable, `stripSearchParams` takes a plain object). In a long-lived
 * process this can go stale across a midnight boundary, so a "today" navigation may keep an
 * explicit `?date=` in the URL instead of stripping it; this is a cosmetic URL nit only (the
 * `date` value itself, and everything read from `Route.useSearch()`/loaderDeps, is always fresh
 * via `lessonsSearchSchema`'s lazy default above). Follow-up: thread a fresh comparison value
 * through a custom search middleware if this needs to be fully precise.
 */
export const LESSONS_SEARCH_DEFAULTS = {
  date: getDefaultLessonsDate(),
  dateFilterValue: DateFilterValue.Equal,
  highlightLesson: null,
};

export type LessonsSearch = z.infer<typeof lessonsSearchSchema>;
